import { useRef } from 'react';

import { cn } from '@ibas/ui';
import { NavyAtmosphere } from '@/components/motion';
import { useReducedMotion } from '@/hooks/use-reduced-motion';
import { gsap, useGSAP, ScrollTrigger } from '@/lib/gsap';

export interface WordHeroProps {
  /**
   * The cycling words, revealed and highlighted one at a time as the section is
   * scrolled. Defaults to the IBAS study modes.
   */
  items?: string[];
  /** The fixed lead line that anchors the cycling words. */
  lead?: string;
  className?: string;
}

const DEFAULT_ITEMS = ['online.', 'on-site.', 'blended.', 'at your pace.', 'globally.'];
const DEFAULT_LEAD = 'Study your way —';

/**
 * WordHero — a sticky, scroll-driven word-cycling hero adapting the classic
 * "you can …" pattern to IBAS. A fixed lead ("Study your way —") anchors a
 * vertical stack of study modes that scroll past and highlight one at a time as
 * the reader scrolls; the active word lifts into brand gold while the rest sit
 * quietly in muted grey, closing on the brand campaign line "Connect globally."
 *
 * Motion intent: the vertical travel of the word column literally embodies
 * "study your way" — the reader moves through every route to the qualification,
 * each mode momentarily foregrounded. Implemented with a single pinned GSAP
 * ScrollTrigger (no view-timeline, no styled-jsx). Under reduced motion the
 * whole thing degrades to a static, fully readable list — no pin, no travel.
 */
export function WordHero({ items = DEFAULT_ITEMS, lead = DEFAULT_LEAD, className }: WordHeroProps) {
  const reduced = useReducedMotion();
  const sectionRef = useRef<HTMLElement>(null);
  const pinRef = useRef<HTMLDivElement>(null);
  const stackRef = useRef<HTMLUListElement>(null);
  const wordRefs = useRef<(HTMLLIElement | null)[]>([]);

  useGSAP(
    () => {
      if (reduced) return;
      const section = sectionRef.current;
      const pin = pinRef.current;
      const stack = stackRef.current;
      const words = wordRefs.current.filter(Boolean) as HTMLLIElement[];
      if (!section || !pin || !stack || words.length === 0) return;

      const setY = gsap.quickSetter(stack, 'y', 'px');
      const last = words.length - 1;
      const stepRef = { current: 0 };

      const measure = () => {
        stepRef.current =
          words.length > 1 ? words[1].offsetTop - words[0].offsetTop : words[0].offsetHeight;
      };

      // Continuous position across the word column; distance from it drives the
      // per-word emphasis so highlighting eases rather than snaps.
      const paint = (pos: number) => {
        const active = Math.round(pos);
        for (let i = 0; i < words.length; i += 1) {
          const w = words[i];
          const d = Math.abs(i - pos);
          const on = i === active;
          w.style.opacity = String(gsap.utils.clamp(0.16, 1, 1 - d * 0.5));
          w.style.transform = `scale(${gsap.utils.clamp(0.9, 1, 1 - d * 0.05)})`;
          w.classList.toggle('text-brand-gold', on);
          w.classList.toggle('text-muted-foreground', !on);
        }
        setY(-pos * stepRef.current);
      };

      measure();
      paint(0);

      const trigger = ScrollTrigger.create({
        trigger: section,
        start: 'top top',
        end: () => `+=${Math.max(items.length, 3) * window.innerHeight * 0.85}`,
        pin,
        pinSpacing: true,
        scrub: true,
        invalidateOnRefresh: true,
        onRefresh: measure,
        onUpdate: (self) => paint(self.progress * last),
      });

      return () => {
        trigger.kill();
      };
    },
    { scope: sectionRef, dependencies: [reduced, items] },
  );

  return (
    <section
      ref={sectionRef}
      className={cn(
        'relative isolate overflow-hidden bg-primary text-primary-foreground',
        className,
      )}
      aria-label={`${lead} ${items.join(' ')} Connect globally.`}
    >
      {/* Shared decorative backdrop — see NavyAtmosphere; matches the closing
          CTA section so the two plain-navy "statement" screens read as a pair. */}
      <NavyAtmosphere />

      <div
        ref={pinRef}
        className={cn(
          'relative z-10 flex min-h-screen w-full flex-col items-center justify-center overflow-hidden px-6 py-24',
          reduced && 'min-h-0',
        )}
      >
        <div className="mx-auto flex w-full max-w-5xl flex-col items-center text-center">
          <p className="font-label text-xs tracking-[0.28em] text-brand-gold uppercase sm:text-sm">
            International Business Academy of Switzerland
          </p>

          <h1 className="mt-8 font-display text-4xl leading-[1.1] font-medium sm:text-6xl lg:text-7xl">
            {lead}
          </h1>

          {reduced ? (
            <ul className="mt-6 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 font-display text-4xl leading-[1.15] font-medium sm:text-6xl lg:text-7xl">
              {items.map((word) => (
                <li key={word} className="text-brand-gold">
                  {word}
                </li>
              ))}
            </ul>
          ) : (
            <div
              className="relative mt-6 w-full overflow-hidden font-display text-4xl leading-[1.15] font-medium sm:text-6xl lg:text-7xl"
              // The window is one word-line tall; the column scrolls behind it.
              style={{ height: '1.15em' }}
              aria-hidden="true"
            >
              <ul
                ref={stackRef}
                className="absolute inset-x-0 top-0 flex flex-col items-center will-change-transform"
              >
                {items.map((word, i) => (
                  <li
                    key={word}
                    ref={(el) => {
                      wordRefs.current[i] = el;
                    }}
                    className="text-muted-foreground"
                    style={{ transformOrigin: 'center' }}
                  >
                    {word}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <p className="mt-12 max-w-xl font-sans text-base text-primary-foreground/70 sm:text-lg">
            Connect globally.
          </p>
        </div>
      </div>
    </section>
  );
}
